import Supercluster from 'supercluster';

/** A point fed into the clusterer — an itinerary place reduced to id + coords. */
export interface ClusterInputPoint {
  id: string;
  longitude: number;
  latitude: number;
}

/** A grouped cluster of nearby points, rendered as a single count bubble. */
export interface RenderedCluster {
  kind: 'cluster';
  /** Supercluster's numeric cluster id — pass to `expansionZoom`. */
  clusterId: number;
  longitude: number;
  latitude: number;
  count: number;
}

/** An individual (declustered) point, rendered as a full pin. */
export interface RenderedLeaf {
  kind: 'leaf';
  id: string;
  longitude: number;
  latitude: number;
}

export type RenderedPoint = RenderedCluster | RenderedLeaf;

/** Geographic viewport as [west, south, east, north] longitudes/latitudes. */
export type BBox = [number, number, number, number];

export interface Clusterer {
  /** Clusters + declustered leaves visible in `bbox` at `zoom`. The whole point
   *  of clustering: at low zoom a dense area collapses to a handful of bubbles
   *  (few DOM nodes) instead of thousands of individual markers. */
  pointsIn(bbox: BBox, zoom: number): Array<RenderedPoint>;
  /** The zoom at which `clusterId` breaks apart — fly here to "expand" it. */
  expansionZoom(clusterId: number): number;
}

interface LeafProps {
  id: string;
}

export interface ClustererOptions {
  /** Cluster radius in pixels (supercluster default 40; 60 declutters denser). */
  radiusPx?: number;
  /** Zoom at/above which points never cluster (always individual pins). */
  maxZoom?: number;
}

/** Build a clusterer over `points`. Wraps supercluster so callers deal in plain
 *  `{id, longitude, latitude}` and get back a flat, render-ready union rather
 *  than raw GeoJSON features. Rebuild (new instance) only when the points change
 *  — querying `pointsIn` per viewport is cheap. */
export const createClusterer = (
  points: Array<ClusterInputPoint>,
  options: ClustererOptions = {},
): Clusterer => {
  const index = new Supercluster<LeafProps>({
    radius: options.radiusPx ?? 60,
    maxZoom: options.maxZoom ?? 16,
  });
  index.load(
    points.map((point) => ({
      type: 'Feature' as const,
      properties: { id: point.id },
      geometry: { type: 'Point' as const, coordinates: [point.longitude, point.latitude] },
    })),
  );

  return {
    pointsIn: (bbox, zoom) =>
      index.getClusters(bbox, Math.round(zoom)).map((feature): RenderedPoint => {
        // A Point's coordinates are always [lng, lat]; defaults only satisfy
        // noUncheckedIndexedAccess.
        const [longitude = 0, latitude = 0] = feature.geometry.coordinates;
        const props = feature.properties;
        if ('cluster' in props && props.cluster) {
          return {
            kind: 'cluster',
            clusterId: props.cluster_id,
            longitude,
            latitude,
            count: props.point_count,
          };
        }
        return { kind: 'leaf', id: (props as LeafProps).id, longitude, latitude };
      }),
    expansionZoom: (clusterId) => index.getClusterExpansionZoom(clusterId),
  };
};

/** Anything with a membership test — satisfied by both `Set` and `Map`, so
 *  callers can pass whichever lookup they already maintain. */
interface IdLookup {
  has(id: string): boolean;
}

/** Whether a marker must always render as its own pin rather than being folded
 *  into a count bubble.
 *
 *  Two cases, for the same underlying reason — the map should never hide what
 *  the user is looking at:
 *   - trip places, because that's the user's own itinerary (and there are few)
 *   - the selected place, because a cluster must never be the reason you can't
 *     see the pin you just clicked
 *
 *  Exempting the selection here rather than relying on zoom is deliberate. The
 *  select-and-fly lands at a zoom floor that sits *below* CLUSTER_MAX_ZOOM, so
 *  in a dense area the pin you just tapped could be swallowed on arrival.
 *  Raising that floor past the threshold would fix today's symptom via an
 *  invisible coupling between two unrelated constants, and would silently break
 *  the next time clustering is re-tuned. Excluding the point from the index is
 *  true at any zoom, and keeps the bubble's count honest. */
export const isNeverClustered = (
  id: string,
  tripPlaceIds: IdLookup,
  selectedId: string | null,
): boolean => tripPlaceIds.has(id) || id === selectedId;

/** Compact a cluster count for the bubble label: 1_500 → "1.5k", 12_000 → "12k". */
export const formatClusterCount = (count: number): string => {
  if (count < 1000) return String(count);
  const thousands = count / 1000;
  return `${thousands >= 10 ? Math.round(thousands) : Math.round(thousands * 10) / 10}k`;
};
